import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faSignOutAlt, faBars, faBell, faEnvelopeOpenText, faMoon } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import api from '../utils/api';
import { disconnectSocket } from '../utils/socket';
import { subscribeActions, refreshActions, resetActions } from '../utils/notificationActions';
import Avatar from './Avatar';
import { clearChatStore } from '../utils/chatStore';
import { applyTheme, getTheme, setTheme } from '../utils/theme';

const Topbar = ({ onToggleSidebar, isSidebarOpen }) => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem('user'));
    const userName = userData?.name || "User";
    
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const notifRef = useRef(null);
    const profileRef = useRef(null);
    const [showProfile, setShowProfile] = useState(false);
    const [theme, updateTheme] = useState(getTheme);

    useEffect(() => {
        const syncTheme = () => updateTheme(applyTheme(getTheme()));
        const systemTheme = window.matchMedia?.('(prefers-color-scheme: dark)');
        window.addEventListener('storage', syncTheme);
        systemTheme?.addEventListener?.('change', syncTheme);
        return () => {
            window.removeEventListener('storage', syncTheme);
            systemTheme?.removeEventListener?.('change', syncTheme);
        };
    }, []);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
            if (profileRef.current && !profileRef.current.contains(event.target)) setShowProfile(false);
        };
        const onEscape = event => {
            if (event.key === 'Escape') { setShowProfile(false); setShowNotifications(false); }
        };
        document.addEventListener('keydown', onEscape);
        document.addEventListener("mousedown", handleClickOutside);
        // Shared with the Notification Center — polling, focus and live pushes
        // are handled once, in utils/notificationActions.
        const off = subscribeActions(s => {
            setNotifications(s.rows);
            setUnreadCount(s.unread);
        });
        return () => {
            off();
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener('keydown', onEscape);
        };
    }, []);

    const fetchNotifications = () => refreshActions({ force: true });

    const markSingleRead = async (notif) => {
        try {
            if (!notif.requiresAction) await api.put(`/notifications/read/${notif._id}`);
            else if (!notif.isRead) await api.put('/notifications/views/read', { view: 'actions', key: notif._id });
            fetchNotifications();
            setShowNotifications(false); // Close dropdown
            
            // Determine route based on type
            // An explicit link always wins — it can point at a specific record.
            let route = '/dashboard';
            if (notif.link) route = notif.link;
            else if (notif.type === 'SALARY') route = '/payroll';
            else if (notif.type === 'WFH') route = '/wfh';
            else if (notif.type === 'LEAVE') route = '/leaves';
            else if (notif.type === 'SHORT_LEAVE') route = '/attendance';
            else if (notif.type === 'TASK') route = '/my-tasks';

            navigate(route);
        } catch (error) {
            console.error("Failed to mark read");
        }
    };

    const handleLogout = () => {
        Swal.fire({
            title: 'Logout?',
            text: "Are you sure you want to end your session?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#215D7B',
            cancelButtonColor: '#A6477F',
            confirmButtonText: 'Yes, Logout',
            cancelButtonText: 'Stay logged in'
        }).then(async (result) => {
            if (result.isConfirmed) {
                // End the session on the server, so this sign-in stops working everywhere
                // it was copied to. Never holds up signing out.
                await Promise.race([
                    api.post('/auth/logout').catch(() => {}),
                    new Promise((resolve) => setTimeout(resolve, 3000))
                ]);
                // Drop the socket too, so the next user doesn't inherit this session.
                disconnectSocket();
                resetActions();
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                clearChatStore();
                navigate('/login');
                window.location.reload();
            }
        });
    };

    return (
        <div className="topbar">
            <div className="topbar-left-section">
                {/* Mobile Menu Trigger */}
                <button className="menu-toggle" onClick={onToggleSidebar} aria-label="Open navigation" aria-expanded={isSidebarOpen} aria-controls="app-sidebar">
                    <FontAwesomeIcon icon={faBars} />
                </button>
                <div className="topbar-logo">
                    <img src="/GTS.png" alt="GTS Logo" />
                </div>
            </div>

            <div className="topbar-right-section" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                
                {/* NOTIFICATIONS BELL */}
                <div className="notification-wrapper" ref={notifRef} style={{ position: 'relative' }}>
                    <div 
                        className="notification-icon" 
                        role="button" tabIndex={0} aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`} aria-expanded={showNotifications}
                        onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setShowNotifications(value => !value); } }}
                        onClick={() => setShowNotifications(!showNotifications)}
                        style={{ cursor: 'pointer', fontSize: '1.2rem', color: 'light-dark(#64748b, var(--theme-muted))', position: 'relative' }}
                    >
                        <FontAwesomeIcon icon={faBell} />
                        {unreadCount > 0 && (
                            <span className="notification-pulse">
                                {unreadCount}
                            </span>
                        )}
                    </div>

                    {showNotifications && (
                        <div className="notification-dropdown">
                            <div className="notification-header">
                                <h6>Action required</h6>
                            </div>
                            <div className="notification-body">
                                {notifications.length > 0 ? (
                                    notifications.slice(0, 4).map(notif => (
                                        <div 
                                            key={notif._id} 
                                            className={`notification-item ${!notif.isRead ? 'unread' : ''}`}
                                            onClick={() => markSingleRead(notif)}
                                        >
                                            <p className="notif-title">{notif.title}</p>
                                            <p className="notif-message">{notif.message}</p>
                                            <span className="notif-time">{new Date(notif.createdAt).toLocaleDateString('en-GB')}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-notifications">
                                        <FontAwesomeIcon icon={faEnvelopeOpenText} />
                                        <p>No new notifications</p>
                                    </div>
                                )}
                            </div>
                            <div className="notification-footer">
                                <button className="notif-btn" onClick={() => { setShowNotifications(false); navigate('/notifications'); }}>Show All</button>
                            </div>
                        </div>
                    )}
                </div>

                <div className={`profile-trigger ${showProfile ? 'profile-open' : ''}`} ref={profileRef}>
                    <div className="profile-trigger-wrapper" role="button" tabIndex={0} aria-label="Account menu" aria-expanded={showProfile}
                        onClick={() => setShowProfile(value => !value)}
                        onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setShowProfile(value => !value); } }}>
                        <span className="user-greeting-name">
                            Hi, {userName.split(' ')[0]}
                        </span>
                        <Avatar name={userName} profilePic={userData?.profilePic} className="profile-badge" />
                    </div>

                    <div className="dropdown-menu">
                        <div className="dropdown-header">
                            <p className="user-full-name">{userName}</p>
                            <p className="user-role-tag">{userData?.role}</p>
                        </div>
                        
                        <Link to="/profile" className="dropdown-link" onClick={() => setShowProfile(false)}>
                            <FontAwesomeIcon icon={faUserCircle} className="dropdown-icon" /> My Profile
                        </Link>

                        <label className="dropdown-link theme-menu-row">
                            <FontAwesomeIcon icon={faMoon} className="dropdown-icon" />
                            <span>Dark mode</span>
                            <input
                                type="checkbox"
                                role="switch"
                                aria-label="Dark mode"
                                checked={theme === 'dark'}
                                onChange={(event) => updateTheme(setTheme(event.target.checked ? 'dark' : 'light'))}
                            />
                            <span className="theme-switch-track" aria-hidden="true" />
                        </label>
                        
                        <div className="dropdown-divider"></div>
                        
                        <div className="dropdown-link logout-text" onClick={handleLogout}>
                            <FontAwesomeIcon icon={faSignOutAlt} className="dropdown-icon" /> Logout
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Topbar;
